const ACTION_FIELDS = ['actions', 'bonusActions', 'reactions'];

const ensureArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const normalizeActionTypeLabel = (value, fallbackField = 'actions') => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');

  if (normalized.includes('bonus')) return 'bonusActions';
  if (normalized.includes('reaction')) return 'reactions';
  if (normalized.includes('action')) return 'actions';

  return fallbackField;
};

const buildEntryKey = (entry, fallbackField = 'actions') => {
  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const name = String(entry.name || entry.title || entry.label || '')
    .trim()
    .toLowerCase();
  const type = String(entry.type || entry.category || '')
    .trim()
    .toLowerCase();
  const activationType = normalizeActionTypeLabel(
    entry.activationType || entry.actionType || entry.sourceSection,
    fallbackField
  );
  const source = String(entry.source || entry.sourceSection || '')
    .trim()
    .toLowerCase();

  if (name) {
    return `${activationType}|${name}|${type}|${source}`;
  }

  if (entry.id) {
    return `${activationType}|${String(entry.id).trim().toLowerCase()}`;
  }

  return '';
};

const decorateActionEntry = (entry, field) => {
  if (!entry || typeof entry !== 'object') return entry;

  const normalizedField = normalizeActionTypeLabel(
    entry.activationType || entry.actionType,
    field
  );

  if (normalizedField === 'bonusActions') {
    return {
      ...entry,
      actionType: entry.actionType || 'bonus_action',
      activationType: entry.activationType || 'bonus action'
    };
  }

  if (normalizedField === 'reactions') {
    return {
      ...entry,
      actionType: entry.actionType || 'reaction',
      activationType: entry.activationType || 'reaction'
    };
  }

  return {
    ...entry,
    actionType: entry.actionType || 'action',
    activationType: entry.activationType || 'action'
  };
};

const pushEntries = (bucket, field, entries, seenKeys) => {
  ensureArray(entries).forEach((entry) => {
    const targetField = normalizeActionTypeLabel(
      entry?.activationType || entry?.actionType,
      field
    );
    const normalizedEntry = decorateActionEntry(entry, targetField);
    const entryKey = buildEntryKey(normalizedEntry, targetField);

    if (entryKey && seenKeys[targetField].has(entryKey)) {
      return;
    }

    if (entryKey) {
      seenKeys[targetField].add(entryKey);
    }

    bucket[targetField].push(normalizedEntry);
  });
};

const getSourceEntries = (source, field) => {
  if (!source || typeof source !== 'object') {
    return [];
  }

  if (field === 'actions') {
    if (Array.isArray(source.actions)) {
      return source.actions;
    }
    if (source.actions && Array.isArray(source.actions.actions)) {
      return source.actions.actions;
    }
    return [];
  }

  if (Array.isArray(source[field])) {
    return source[field];
  }
  if (source.actions && Array.isArray(source.actions[field])) {
    return source.actions[field];
  }

  return [];
};

export const extractActionEconomy = (...sources) => {
  const bucket = {
    actions: [],
    bonusActions: [],
    reactions: []
  };
  const seenKeys = {
    actions: new Set(),
    bonusActions: new Set(),
    reactions: new Set()
  };

  sources.forEach((source) => {
    ACTION_FIELDS.forEach((field) => {
      pushEntries(bucket, field, getSourceEntries(source, field), seenKeys);
    });
  });

  return bucket;
};

export const mergeActionEconomy = (...economies) => {
  const bucket = {
    actions: [],
    bonusActions: [],
    reactions: []
  };
  const seenKeys = {
    actions: new Set(),
    bonusActions: new Set(),
    reactions: new Set()
  };

  economies.forEach((economy) => {
    if (!economy || typeof economy !== 'object') return;
    ACTION_FIELDS.forEach((field) => {
      pushEntries(bucket, field, economy[field], seenKeys);
    });
  });

  return bucket;
};

export const flattenActionEconomy = (economy) => {
  if (!economy || typeof economy !== 'object') {
    return [];
  }

  return [
    ...ensureArray(economy.actions).map((entry) => decorateActionEntry(entry, 'actions')),
    ...ensureArray(economy.bonusActions).map((entry) => decorateActionEntry(entry, 'bonusActions')),
    ...ensureArray(economy.reactions).map((entry) => decorateActionEntry(entry, 'reactions'))
  ];
};

export default extractActionEconomy;
